/**
 * Correspondência avançada da Meta na vitrine (Pixel do navegador).
 *
 * **Por que existe:** no Gerenciador de Eventos, PageView/ViewContent/
 * AddToCart tinham qualidade de correspondência 6,1/10 — e-mail em 0,27% dos
 * eventos. No checkout/orderPlaced a VTEX já manda o e-mail (9,0–9,3/10); na
 * vitrine headless ninguém mandava. Só o cliente logado (#186) subiu o e-mail
 * pra ~2% — a maioria navega sem login.
 *
 * **De onde vêm os dados** (uma vez por carga, no efeito do RootLayout):
 *  - **Logado:** `getUserFromJwt` (server fn que só decodifica o cookie de
 *    login da VTEX, sem chamada externa) → e-mail + userId.
 *  - **Já passou pelo checkout neste navegador:** `fbeEventUserEmail`, que o
 *    pixel do FBE grava quando o cliente digita o e-mail no checkout.
 *  - **Todo visitante:** `external_id` = `dt_visitor_id` (id próprio do site,
 *    1º-party) quando não há login — só com ele já é um identificador estável.
 *
 * **Pra onde vão:**
 *  - **Servidor:** o e-mail do logado vai pra `fbeEventUserEmail`, que
 *    `~/sdk/metaCapi` já manda como `email` pro `vtex.facebook-capi`.
 *  - **Navegador:** `user_data` SÓ com SHA-256 ({em, external_id}) é gravado
 *    em `dt_meta_user_data` no localStorage — a tag base do Pixel ("AT | FB -
 *    PageView", GTM) lê isso no `fbq("init", ...)`, então os dados já valem
 *    no PageView e no primeiro ViewContent da próxima página. Quando o valor
 *    MUDA, também empurra `{event: "dt_user_data", user_data}` pra tag "AT |
 *    FB - UserData" re-inicializar o Pixel na página atual. E-mail em texto
 *    nunca vai pro dataLayer nem pra esse cache.
 *
 * **Logout:** se o e-mail foi gravado por ESTE módulo (marca abaixo) e a
 * visita seguinte já não tem login, apaga — sem mexer no e-mail que o FBE
 * gravou pra comprador sem conta no checkout.
 *
 * Respeita a recusa do banner de cookies (`hasCookieConsent`, opt-out): com
 * recusa, apaga o cache e não faz mais nada (o GTM nem carrega, #191).
 * Nunca lança: roda dentro do efeito de ROOT (ver __root.tsx).
 */
import { invoke } from "~/server/invoke";
import { FBE_EMAIL_STORAGE_KEY } from "~/sdk/metaCapi";
import { hasCookieConsent } from "~/utils/consent";
import { getOrCreateVisitorId } from "~/utils/visitorId";

/** Marca que o e-mail salvo veio do login (e não do FBE no checkout). */
export const EMAIL_FROM_LOGIN_KEY = "dt_meta_email_from_login";
/** Cache SÓ com hashes, lido pela tag base do Pixel no GTM. */
export const USER_DATA_CACHE_KEY = "dt_meta_user_data";
export const USER_DATA_EVENT = "dt_user_data";

export interface MetaUserData {
  em?: string;
  external_id?: string;
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function usableEmail(email: string | null | undefined): string | null {
  const e = email?.trim().toLowerCase();
  return e && e.indexOf("@") > 0 && !e.includes("*") ? e : null;
}

/**
 * Pura (exceto o hash): normalização que a Meta pede (trim + minúsculas no
 * e-mail). `external_id` = userId do logado ou, sem login, o id de visitante.
 * Devolve `null` se não houver nada pra mandar.
 */
export async function buildMetaUserData(
  input: { email?: string | null; userId?: string | null; visitorId?: string | null },
): Promise<MetaUserData | null> {
  const data: MetaUserData = {};
  const email = usableEmail(input.email);
  if (email) data.em = await sha256Hex(email);
  const externalId = input.userId?.trim() || input.visitorId?.trim();
  if (externalId) data.external_id = await sha256Hex(externalId);
  return data.em || data.external_id ? data : null;
}

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage bloqueado -- o Pixel só recebe pelo dt_user_data desta página
  }
}

function storageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // idem
  }
}

export async function syncMetaUserData(): Promise<void> {
  try {
    if (typeof window === "undefined") return;
    if (!hasCookieConsent()) {
      storageRemove(USER_DATA_CACHE_KEY);
      return;
    }

    const user = await invoke.site.loaders.getUserFromJwt();
    const loginEmail = usableEmail(user?.email);

    if (loginEmail) {
      storageSet(FBE_EMAIL_STORAGE_KEY, loginEmail);
      storageSet(EMAIL_FROM_LOGIN_KEY, "1");
    } else if (storageGet(EMAIL_FROM_LOGIN_KEY) === "1") {
      // Logout: o e-mail salvo era do login, não do checkout do FBE.
      storageRemove(FBE_EMAIL_STORAGE_KEY);
      storageRemove(EMAIL_FROM_LOGIN_KEY);
    }

    const userData = await buildMetaUserData({
      email: loginEmail ?? storageGet(FBE_EMAIL_STORAGE_KEY),
      userId: loginEmail ? user?.userId : null,
      visitorId: getOrCreateVisitorId(),
    });

    const serialized = userData ? JSON.stringify(userData) : null;
    if (serialized === storageGet(USER_DATA_CACHE_KEY)) return; // a tag base já usou

    if (serialized) storageSet(USER_DATA_CACHE_KEY, serialized);
    else storageRemove(USER_DATA_CACHE_KEY);

    if (userData) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: USER_DATA_EVENT, user_data: userData });
    }
  } catch (error) {
    console.error("[metaUserData] falha ao sincronizar dados do cliente:", error);
  }
}
